import { useState, useId } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { ActionButton, ErrorBox, Load, Page, useApi } from '../ui/kit';
import { BoardCards, DayBars, askAssistant, useBoard } from '../ui/board';
import { FirstBatch } from '../ui/first-batch';
import { CampaignAngles } from '../ui/angles';
import { ProtectionNotice } from './sending-health';
import { CampaignDepth, CampaignLeads, CampaignResults, CreditForecast, type CampaignStats } from '../ui/outcomes';
import { CampaignSignals } from '../ui/signals';
import { CampaignAbTest } from '../ui/ab-test';
import { CampaignLinkedin } from '../ui/linkedin';

// Campaigns in as few steps as the rules allow. Creating one is one form (the audience; sending defaults filled in),
// then Outlevo drafts the messages. The approvals the rules require stay, shown on the campaign as one checklist
// with the next action's button.

type Mailbox = { id: string; email: string; connected: boolean; daily_cap: number | null };

const DEFAULT_SCHEDULE = () => ({
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
  days: [1, 2, 3, 4, 5], start_hour: '09:00', end_hour: '17:00', min_minutes_between: 10, max_new_leads_per_day: 50,
});

export function CampaignNew() {
  const uid = useId(); // every field has an id: its wrapping label or aria-label is its only name
  const nav = useNavigate();
  const qc = useQueryClient();
  const account = useApi<{ connected?: boolean; status?: string } | null>('/v1/outbound/account');
  const boxes = useApi<{ data: Mailbox[] }>('/v1/mailboxes');
  const setup = useApi<{ markets: string[] }>('/portal/site-setup');
  const sending = useApi<{ managed_available: boolean }>('/v1/sending');
  const managed = sending.data?.managed_available !== false;
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const connected = (boxes.data?.data ?? []).filter((b) => b.connected);

  const connect = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault(); setErr(null); setBusy(true);
    try {
      await api('/portal/outbound/connect', { method: 'POST', body: { api_key: String(new FormData(e.currentTarget).get('api_key')) } });
      await qc.invalidateQueries();
    } catch (x) { setErr(x); } finally { setBusy(false); }
  };
  const create = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault(); setErr(null); setBusy(true);
    const f = new FormData(e.currentTarget);
    const schedule = { ...DEFAULT_SCHEDULE(), ...(advanced ? {
      start_hour: String(f.get('start_hour')), end_hour: String(f.get('end_hour')), max_new_leads_per_day: Number(f.get('per_day')),
    } : {}) };
    try {
      const c = await api<{ id: string }>('/v1/campaigns', { method: 'POST', body: { name: String(f.get('name')).trim(), mailbox_ids: connected.map((b) => b.id), schedule } });
      // the messages are drafted right away when an outbound policy is approved; otherwise the checklist says what is missing
      await api(`/v1/campaigns/${c.id}/draft-sequence`, { method: 'POST' }).catch(() => undefined);
      nav(`/campaigns/${c.id}`);
    } catch (x) { setErr(x); } finally { setBusy(false); }
  };

  const markets = setup.data?.markets ?? [];
  const board = useBoard();
  return (
    <Page title="New campaign" actions={<button type="button" className="btn btn-primary" onClick={() => askAssistant('Set up a new campaign with me')}>Set it up with the assistant</button>}>
      <p className="muted">The assistant on the right fills these cards in with you, one question at a time.</p>
      {board.data ? <BoardCards board={board.data} /> : board.error ? <ErrorBox error={board.error} /> : <p className="muted" role="status">Loading…</p>}
      <details className="section new-campaign"><summary>Or fill in the campaign yourself</summary>
        {!connected.length ? (
          <form onSubmit={connect} className="form">
            <h2>First, set up your sending</h2>
            {managed ? <>
              <p className="muted">Outlevo sets sending up for you: it buys lookalike domains and mailboxes included in your plan, and warms them up for 2–3 weeks.</p>
              <p><button type="button" className="btn btn-primary" onClick={() => askAssistant('Set up sending for me')}>Set up sending with the assistant</button></p>
            </> : <p className="muted">Outlevo-run mailboxes open soon. Meanwhile, connect your own Smartlead below.</p>}
            <p className="muted small">Advanced: already have warmed-up mailboxes in Smartlead? Paste your Smartlead API key; it is stored encrypted and never shown again.</p>
            <div className="field"><label htmlFor="sl">Smartlead API key</label><input id="sl" name="api_key" required minLength={10} autoComplete="off" /></div>
            <button type="submit" className="btn" disabled={busy}>{busy ? 'Connecting…' : 'Connect my own Smartlead'}</button>
            {account.data?.status && account.data.status !== 'not_connected' ? <p className="muted small">Connected, but no mailbox is ready yet: add one in Smartlead, then come back.</p> : null}
          </form>
        ) : (
          <form onSubmit={create} className="form">
            <div className="field">
              <label htmlFor="cn">Who is this campaign for?</label>
              <input id="cn" name="name" required maxLength={200} placeholder="e.g. Heads of RevOps at mid-market SaaS" />
              <span className="muted small">Name the audience. Outlevo drafts the messages from your approved templates and business context.</span>
            </div>
            <div className="field"><span className="label">Where</span>
              <p className="muted">{markets.length ? <>Your ICP markets: <b>{markets.join(', ')}</b> · <Link to="/icp">change</Link></> : <>Set your markets on the <Link to="/icp">ICP</Link> first.</>}</p>
            </div>
            <div className="field"><span className="label">Sending</span>
              <p className="muted">From {connected.length} mailbox{connected.length === 1 ? '' : 'es'} ({connected.map((b) => b.email).join(', ')}), Mon–Fri 9:00–17:00 your time, up to 50 new leads a day. <button type="button" className="link" onClick={() => setAdvanced(!advanced)}>{advanced ? 'Use the defaults' : 'Change'}</button></p>
              {advanced ? (
                <div className="caps">
                  <label>From <input id={`${uid}-f1`} type="time" name="start_hour" defaultValue="09:00" /></label>
                  <label>To <input id={`${uid}-f2`} type="time" name="end_hour" defaultValue="17:00" /></label>
                  <label>New leads a day <input id={`${uid}-f3`} type="number" name="per_day" min={1} max={1000} defaultValue={50} /></label>
                </div>
              ) : null}
            </div>
            <div className="form-actions"><Link className="btn" to="/campaigns">Cancel</Link><button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Creating…' : 'Create campaign'}</button></div>
          </form>
        )}
        {err ? <ErrorBox error={err} /> : null}
      </details>
    </Page>
  );
}

type Campaign = {
  id: string; name: string; status: string; first_campaign: boolean; reviewed_at: string | null;
  sequence: Array<{ seq_number: number; delay_in_days: number; subject: string; body: string }>;
  problems: { approval: string[]; senders: string[] };
  /** domain protection: why the guard paused it, what to check */
  protection?: { reason: string; checks: string[]; scope: string } | null;
  /** research depth: researched (default) or volume, and what one contact costs at it */
  depth?: string; per_contact?: { credits: number };
};
type Stats = CampaignStats & { pushed: number; bounced: number; reply_rate: number | null };

/** src/outbound/review-copy.ts AI_REVIEW_NOTE (the same sentence the assistant and the MCP notes use). */
export const AI_REVIEW_NOTE = "Flagged emails are reviewed by Outlevo's AI reviewer within minutes; anything it can't clear safely comes back to you with what to change.";
/** How the problems begin when the AI reviewer sent the emails back (src/outbound/service.ts AI_COULD_NOT_CLEAR). */
const COULD_NOT_CLEAR = 'Outlevo\'s AI reviewer could not clear the emails:';

/** The review step: cleared, sent back by the AI reviewer with what to change, or being reviewed. */
export function reviewGateDetail(c: Pick<Campaign, 'reviewed_at' | 'sequence' | 'problems'>): string {
  if (c.reviewed_at) return 'Cleared.';
  const back = c.problems.approval.find((p) => p.startsWith(COULD_NOT_CLEAR));
  if (back) return back;
  return c.sequence.length ? AI_REVIEW_NOTE : `Drafted emails go through the automatic check. ${AI_REVIEW_NOTE}`;
}

/** One campaign: what it sends, and the remaining gates as one checklist with the next action. */
export function CampaignFlow() {
  const { id } = useParams();
  const stats = useApi<Stats>(`/v1/campaigns/${id}/stats`);
  const board = useBoard(id);
  return (
    <Load<Campaign> path={`/v1/campaigns/${id}`}>{(c) => {
      const has = (s: string) => c.problems.approval.some((p) => p.includes(s));
      const drafted = c.sequence.length > 0;
      const reviewed = Boolean(c.reviewed_at);
      const needsMessagesApproval = has('exact messages approved');
      const policyMissing = has('outbound policy');
      const sendersReady = !c.problems.senders.length;
      const built = ['built', 'active', 'paused'].includes(c.status);
      const gates: Array<{ label: string; done: boolean; detail: React.ReactNode }> = [
        { label: 'Outbound policy approved', done: !policyMissing, detail: policyMissing ? <span>A sales approver approves templates, a footer and caps once. <Link to="/approvals">Open approvals</Link></span> : 'Templates, footer and caps are approved.' },
        { label: 'Messages drafted', done: drafted, detail: drafted ? `${c.sequence.length} steps` : <ActionButton path={`/v1/campaigns/${id}/draft-sequence`} label="Draft messages" variant="primary" /> },
        { label: 'Checked by the automatic check and AI reviewer', done: reviewed, detail: reviewGateDetail(c) },
        ...(c.first_campaign || needsMessagesApproval ? [{ label: 'You approve the exact messages (first campaign)', done: reviewed && !needsMessagesApproval, detail: !reviewed ? 'After the check above.' : needsMessagesApproval ? <ActionButton path={`/v1/campaigns/${id}/submit-messages`} label="Send for approval" variant="primary" onDone={(r) => r?.policy_id && location.assign(`/approvals/${r.policy_id}`)} /> : 'Approved.' }] : []),
        { label: 'Senders ready', done: sendersReady, detail: sendersReady ? 'Mailboxes warmed up with daily caps; SPF, DKIM and DMARC pass.' : <ul className="tight">{c.problems.senders.map((p) => <li key={p}>{p}</li>)}</ul> },
        { label: 'Built in Smartlead', done: built, detail: built ? 'Created, not sending until you start it.' : !c.problems.approval.length ? <ActionButton path={`/v1/campaigns/${id}/build`} label="Build" variant="primary" /> : 'After the approvals above.' },
      ];
      const next = gates.findIndex((g) => !g.done);
      return (
        <Page title={c.name} actions={<>
          {c.status === 'built' || c.status === 'paused' ? <ActionButton path={`/portal/campaigns/${id}/start`} label="Start sending" variant="primary" confirm="Start sending? Every rule is checked again first." /> : null}
          {c.status === 'active' ? <ActionButton path={`/v1/campaigns/${id}/pause`} label="Pause" variant="danger" /> : null}
        </>}>
          {c.status === 'paused' ? <ProtectionNotice p={c.protection} /> : null}
          <CreditForecast campaignId={id} />
          <p className="muted"><span className={`chip ${c.status === 'active' ? 'done' : ''}`}>{c.status}</span>{board.data?.mode === 'manage' ? ' · the assistant can analyse it: ask it how the campaign is doing' : ''}</p>
          {c.depth ? <CampaignDepth depth={c.depth} perContact={c.per_contact} usdPerCredit={stats.data?.usd_per_credit}
            onChange={() => askAssistant(`Should "${c.name}" stay ${c.depth}, or switch to ${c.depth === 'volume' ? 'researched' : 'volume'}? What would change?`)} /> : null}
          {['built', 'active', 'paused'].includes(c.status) ? <FirstBatch campaignId={id!} /> : null}
          {board.data ? <BoardCards board={board.data} extra={{
            launch: <ol className="gates">{gates.map((g, i) => (
              <li key={g.label} className={g.done ? 'done' : i === next ? 'next' : ''}>
                <span className="own-n" aria-hidden="true">{g.done ? '✓' : i + 1}</span>
                <div><b>{g.label}</b><div className="muted small">{g.detail}</div></div>
              </li>
            ))}</ol>,
            messages: <>
              <CampaignAngles campaignId={id!} />
              {drafted ? c.sequence.map((s) => (
                <div key={s.seq_number} className="msg"><div className="msg-head"><b>Step {s.seq_number}</b><span className="muted"> · day {s.delay_in_days}</span></div><div className="msg-subject">{s.subject}</div><pre className="body">{s.body}</pre></div>
              )) : null}
            </>,
            performance: <>
              {stats.data ? <CampaignResults s={stats.data} /> : null}
              {board.data.days ? <DayBars days={board.data.days} /> : null}
            </>,
          }} /> : board.error ? <ErrorBox error={board.error} /> : <p className="muted" role="status">Loading the campaign…</p>}
          <CampaignAbTest campaignId={id!} />
          <CampaignSignals campaignId={id!} />
          <CampaignLinkedin campaignId={id!} />
          <CampaignLeads campaignId={id!} />
        </Page>
      );
    }}</Load>
  );
}
